import { jsxs, jsx } from 'react/jsx-runtime';
import ndsTokens from 'nicollet-tokens';
import styled, { css } from 'styled-components';
import { ndsFormStyles, ndsFontReset, ndsBoxSizing, ndsScreenReaderOnly, ScreenReaderOnly } from '@nicollet/common';

const disabledStyles = `
  background: ${ndsFormStyles.colors.background.disabled};
  color: ${ndsTokens.colors.text.onLight.secondary};
  cursor: not-allowed;
`;
const errorStyles = `
  color: ${ndsFormStyles.colors.alert.warning.text};
  background-color: ${ndsFormStyles.colors.alert.warning.background};
`;
const animatedStyles = `
  background-color: ${ndsTokens.colors.background.default};
  backface-visibility: hidden;
  left: 0;
  margin-left: ${ndsTokens.space.generic.x2};
  position: absolute;
  pointer-events: none;
  transform: translate3d(0, 10px, 0);
  transition: all 200ms ease-out;
  white-space: nowrap;
  z-index: 1;
`;
const isFocusedStyles = `
  font-size: ${ndsTokens.font.size.text.small};
  transform: translate3d(0, -8px, 0);
  /* Visual spacing when the label is raised up */
  padding-left: ${ndsTokens.space.generic.x1};
  padding-right: ${ndsTokens.space.generic.x1};
`;
const labelStyles = css`
  ${ndsFontReset()}
  ${ndsBoxSizing}
  display: inline-block;
  font-size: ${ndsTokens.font.size.text.base};
  color: ${ndsTokens.colors.text.onLight.primary};
  line-height: ${ndsTokens.font.lineHeight.text};
  overflow: hidden;

  ${({
  hidden
}) => hidden && ndsScreenReaderOnly()};
  ${({
  animated
}) => animated && animatedStyles};
  ${({
  valid,
  animated
}) => valid === false && animated && errorStyles};
  ${({
  isFocused
}) => isFocused && isFocusedStyles};
  ${({
  disabled
}) => disabled && disabledStyles};
`;
const StyledLabel = styled.label`
  ${labelStyles}
`;
const StyledLabelSpan = styled.span`
  ${labelStyles}
`;
const StyledLabelLegend = styled.legend`
  ${labelStyles}
`;

const Label = ({
  className,
  htmlFor,
  label,
  hidden,
  disabled,
  disableLiveRegion,
  microcopy,
  screenReader,
  errorText,
  valid,
  animated,
  isFocused,
  tag = 'label',
  'data-test': testId
}) => {
  const tagMapping = {
    label: StyledLabel,
    span: StyledLabelSpan,
    legend: StyledLabelLegend
  }; // TODO: This currently maps to 'label', 'span' or 'legend'. But 'label' is the only one being used. A refactor and deprecation seems like the best path forward.

  const TagName = tagMapping[tag] || 'label';
  return jsxs(TagName, Object.assign({
    animated: animated,
    className: className,
    "data-test": testId,
    disabled: disabled,
    hidden: hidden,
    htmlFor: htmlFor,
    isFocused: isFocused,
    valid: valid
  }, {
    children: [label, screenReader && jsx(ScreenReaderOnly, {
      children: screenReader
    }, void 0), microcopy && jsx(ScreenReaderOnly, {
      children: microcopy
    }, void 0), jsx(ScreenReaderOnly, Object.assign({
      "aria-atomic": disableLiveRegion ? undefined : 'true',
      "aria-live": disableLiveRegion ? undefined : 'assertive'
    }, {
      children: valid === false && errorText
    }), void 0)]
  }), void 0);
};

Label.displayName = 'Label';

export { Label };
